import { useMutation, useQuery } from '@apollo/client';
import type { UseFetchDealInsights } from '@openx/components-deal/lib/utils/context/DealHookTypes';
import { handleBqJobResponse } from '@openx/components-deal/lib/utils/handleBqJobResponse';
import type { BqJobResponseApi } from '@openx/types/bqJob';
import type { DealInsightsApi } from '@openx/types/dealInsights';
import { BQ_JOB_MAX_REQUEST_COUNT, BQ_JOB_REFETCH_INTERVAL } from '@openx/utils/services/config';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CancelDealInsightsJobDocument } from '../../../../graphql/query/deals/CancelDealInsightsJob.generated';
import { GetDealInsightsJobDocument } from '../../../../graphql/query/deals/GetDealInsightsJob.generated';

export const useFetchDealInsights: UseFetchDealInsights = ({ job_id, enabled = true }) => {
  const requestsCountRef = useRef<number>(1);
  const { enqueueSnackbar } = useSnackbar();

  const [error, setError] = useState<Error | string | undefined>();
  const [bgJobReadyRes, setBqJobReadyRes] = useState<{ data: DealInsightsApi; job_id: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [cancelDealInsightsJob] = useMutation(CancelDealInsightsJobDocument);

  const jobIdRef = useRef<string | undefined>(job_id);
  const enabledRef = useRef(enabled);
  // Keep in sync during render (not via useEffect): cleanupJob is invoked from other
  // effects reacting to the same render (e.g. useDealInsightsCache's cache-hit cleanup
  // path), so it must always read the current value rather than one from an
  // as-yet-unrun effect - see the "hits cache" cancellation bug this fixes.
  enabledRef.current = enabled;

  const { startPolling, stopPolling } = useQuery(GetDealInsightsJobDocument, {
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      handleBqJobResponse<DealInsightsApi>({
        MAX_REQUEST_COUNT: BQ_JOB_MAX_REQUEST_COUNT,
        continuePollingOnTimeout: true,
        data: data.dealInsightsJob as unknown as BqJobResponseApi<DealInsightsApi>,
        reqCount: requestsCountRef.current,
        stateUpdateCallback,
        stopPolling: () => {
          stopPolling();
          setIsLoading(false);
        },
        updateReqCount: () => {
          requestsCountRef.current += 1;

          //1 minute
          if (requestsCountRef.current === 60) {
            enqueueSnackbar('Data is still being processed. This may take longer than expected.', {
              variant: 'info',
            });
          }
        },
      });
    },
    onError: error => {
      setError(error);
      stopPolling();
      setIsLoading(false);
      // Clear the jobIdRef when there's an error to prevent cleanup issues
      jobIdRef.current = undefined;
    },
    skip: !enabled,
    variables: { job_id },
  });

  const stateUpdateCallback = useCallback(
    ({ errorMessage, data, job_id }: { errorMessage?: string; data?: DealInsightsApi; job_id?: string }) => {
      if (errorMessage) {
        setError(errorMessage);
        // Clear the jobIdRef when there's an error to prevent cleanup issues
        jobIdRef.current = undefined;
      }
      if (data) {
        setBqJobReadyRes({ data, job_id: job_id as string });
        // Clear the jobIdRef when data is successfully loaded to prevent cleanup issues
        jobIdRef.current = undefined;
      }
    },
    []
  );

  const cleanupJob = useCallback(() => {
    if (jobIdRef.current && enabledRef.current) {
      cancelDealInsightsJob({ variables: { jobId: jobIdRef.current } });
    }
  }, [cancelDealInsightsJob]);

  const cleanupPreviousJob = useCallback(
    (previousJobId: string | undefined) => {
      if (previousJobId && previousJobId !== job_id) {
        cancelDealInsightsJob({ variables: { jobId: previousJobId } });
      }
    },
    [job_id, cancelDealInsightsJob]
  );

  useEffect(() => {
    const previousJobId = jobIdRef.current;
    jobIdRef.current = job_id;

    if (previousJobId && previousJobId !== job_id) {
      cleanupPreviousJob(previousJobId);
    }
  }, [job_id, cleanupPreviousJob]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: If job_id or enabled changes, we want to reset the state and startPolling
  useEffect(() => {
    setError(undefined);
    setBqJobReadyRes(undefined);
    requestsCountRef.current = 1;

    if (job_id && enabled) {
      setIsLoading(true);
      startPolling(BQ_JOB_REFETCH_INTERVAL);
    }

    return () => {
      if (job_id) {
        stopPolling();
      }
    };
  }, [job_id, enabled, cancelDealInsightsJob, stopPolling]);

  useEffect(() => {
    return cleanupJob;
  }, [cleanupJob]);

  useEffect(() => {
    window.addEventListener('beforeunload', cleanupJob);

    return () => {
      window.removeEventListener('beforeunload', cleanupJob);
    };
  }, [cleanupJob]);

  const stopPollingCallback = useCallback(() => {
    stopPolling();
    setIsLoading(false);
  }, [stopPolling]);

  return {
    data: bgJobReadyRes ? { dealInsights: bgJobReadyRes.data, job_id: bgJobReadyRes.job_id } : undefined,
    error,
    isLoading,
    cleanupJob,
    stopPolling: stopPollingCallback,
    requestsCount: requestsCountRef.current,
  };
};
